import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Enums matching backend
export enum ReportType {
  ShelfLabel = 1,
  ItemLabel = 2,
  ColliLabel = 3,
  PackingSlip = 4
}

export enum ShelfLabelContentType {
  ItemNumberOnly = 1,
  ItemNumberWithBarcode = 2,
  LocationNameOnly = 3,
  LocationNameWithBarcode = 4
}

export enum PaperSize {
  Label_100x50mm = 1,
  Label_100x150mm = 2,
  A6 = 3,
  A4 = 4
}

// Request interfaces
export interface ShelfLabelRequest {
  contentType: ShelfLabelContentType;
  paperSize: PaperSize;
  itemNo?: string;
  itemDescription?: string;
  locationName?: string;
  binCode?: string;
}

export interface ItemLabelRequest {
  paperSize: PaperSize;
  itemNo: string;
  itemDescription: string;
  batch?: string;
  expiryDate?: string;
  weight?: number;
  quantity?: number;
}

export interface ColliLabelRequest {
  paperSize: PaperSize;
  orderNoERP: string;
  customerName: string;
  deliveryAddress?: string;
  postalCode?: string;
  city?: string;
  colliNumber: number;
  totalColli: number;
  weight?: number;
  sscc?: string;
}

export interface PackingSlipLineDto {
  lineNo: number;
  itemNo: string;
  itemDescription: string;
  qtyOrdered: number;
  qtyPicked: number;
  location?: string;
}

export interface PackingSlipRequest {
  paperSize: PaperSize;
  pListId: number;
  companyNo: string;
  packingDate?: string;
  customerName?: string;
  deliveryAddress?: string;
  lines: PackingSlipLineDto[];
  totalWeight?: number;
  totalColli?: number;
  carrierName?: string;
  remarks?: string;
}

export interface PrintReportRequest {
  printerId: number;
  reportType: ReportType;
  copies?: number;
  shelfLabel?: ShelfLabelRequest;
  itemLabel?: ItemLabelRequest;
  colliLabel?: ColliLabelRequest;
  packingSlip?: PackingSlipRequest;
}

export interface PrintReportResponse {
  success: boolean;
  message: string;
  printJobId?: number;
  pdfBase64?: string;
}

// Helper function to get paper size display name
export const getPaperSizeDisplayName = (paperSize: PaperSize): string => {
  switch (paperSize) {
    case PaperSize.Label_100x50mm:
      return 'Etikett 100x50mm';
    case PaperSize.Label_100x150mm:
      return 'Etikett 100x150mm';
    case PaperSize.A6:
      return 'A6 (105x148mm)';
    case PaperSize.A4:
      return 'A4 (210x297mm)';
    default:
      return 'Ukjent';
  }
};

// Helper function to get shelf label content type display name
export const getShelfLabelContentTypeDisplayName = (contentType: ShelfLabelContentType): string => {
  switch (contentType) {
    case ShelfLabelContentType.ItemNumberOnly:
      return 'Kun artikkelnummer';
    case ShelfLabelContentType.ItemNumberWithBarcode:
      return 'Artikkelnummer med strekkode';
    case ShelfLabelContentType.LocationNameOnly:
      return 'Kun lokasjonsnavn';
    case ShelfLabelContentType.LocationNameWithBarcode:
      return 'Lokasjonsnavn med strekkode';
    default:
      return 'Ukjent';
  }
};

// Helper function to get report type display name
export const getReportTypeDisplayName = (reportType: ReportType): string => {
  switch (reportType) {
    case ReportType.ShelfLabel:
      return 'Hyllekant-etikett';
    case ReportType.ItemLabel:
      return 'Vareetikett';
    case ReportType.ColliLabel:
      return 'Kollietikett';
    case ReportType.PackingSlip:
      return 'Pakkseddel';
    default:
      return 'Ukjent';
  }
};

class ReportService {
  /**
   * Generate and print a report
   */
  async printReport(companyNo: string, request: PrintReportRequest): Promise<PrintReportResponse> {
    const token = AuthService.getToken();
    const response = await fetch(`${API_BASE_URL}/${companyNo}/Report/print`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to print report: ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Generate a report without printing (for preview/download)
   */
  async generateReport(companyNo: string, request: PrintReportRequest): Promise<PrintReportResponse> {
    const token = AuthService.getToken();
    const response = await fetch(`${API_BASE_URL}/${companyNo}/Report/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to generate report: ${errorText}`);
    }

    return await response.json();
  }
}

export default new ReportService();
